import Backbone from 'backbone'
import numericRangeValidatorFactory from '../validators/numeric-range-validator-factory'
import i18n from '../i18n'
import _ from 'lodash'
import MarkedField from '../marked-field'
import numericEvaluator from '../evaluators/numeric-evaluator'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER } = DataType
import isPercentDisplayed from 'scoreboard/components/is-percent-displayed'
import requiredValidator from 'scoreboard/components/validators/required-validator'

const ThresholdField = Backbone.View.extend({

	events: {
		'change input' : '_changeInput'
	},

	/**
	 * @param {Boolean} [options.isShapeWidget=false] used to determine specific
	 * logic for shape widgets, such as when defining lowest and highest
	 * conditional value thresholds
	 * @param {Thresholds} options.thresholds the thresholds model
	 * @param {Number} options.index the index of the threshold
	 * @param {String} [options.dataType] the data type of the threshold
	 * @param {String} [options.currencyCode] the currency code of the thresholds
	 * @param {String} [options.precision] the decimal precision of the
	 * thresholds
	 * @param {Boolean} [options.isGaugeWidget=false] if this is for a dashboards
	 * gauge widget
	 */
	initialize: function(options = {}) {
		this.options = options
		this.thresholds = options.thresholds
		this.index = options.index
		this.isShapeWidget = options?.isShapeWidget ?? false
		this.isGaugeWidget = options?.isGaugeWidget ?? false
		this.value = this.thresholds.value(this.index)
	},

	_validator: function() {
		let {min, max} = this.thresholds.bounds[this.index]
		let formattingOptions = { dataType: this.options.dataType }
		// #58966 - if a dataType was provided, set unformatted to false if the
		// dataType should be display as a percent (all other dataTypes will
		// display unformatted, as they were before)
		const unformatted = !this.options?.dataType ||
			!isPercentDisplayed(this.options?.dataType)
		let rangeValidator = numericRangeValidatorFactory(min, max, formattingOptions, {
			excludeLow: true, excludeHigh:true, excludeBetween:true,
			unformatted, allowBlank: true
		})
		let validators = [rangeValidator]
		// #58969 for dashboards gauge widgets, we want the first and last
		// thresholds to be required
		if (this.isGaugeWidget) {
			const lastIndex = _.size(this.thresholds.values()) - 1
			if (this.index == 0 || this.index == lastIndex) {
				validators.push(requiredValidator)
			}
		}
		return validators
	},

	render: function() {
		const placeholder = this.getPlaceholder()
		this.$el.html(`<input data-threshold-index="${this.index}" type="text" placeholder="${placeholder}">`)
		let input = this.$('input')
		if (_.isFinite(this.value)) {
			input.val(this.value)
		}
		this.markedField = new MarkedField({
			el: input,
			validator: this._validator(),
			evaluator: numericEvaluator,
			dataType: this.options?.dataType ?? NUMBER,
			currencyCode: this.options?.currencyCode,
			precision: this.options?.precision
		}).render()
		return this
	},

	getPlaceholder: function() {
		let placeholder = i18n.get('common.auto')
		let valueCode
		const lastIndex = _.size(this.thresholds.values()) - 1

		// the first and last inputs get special placeholders
		if (this.index == 0) valueCode = 'lowest'
		else if (this.index == lastIndex) valueCode = 'highest'
		if (!_.isNil(valueCode)) {
			const msgCode = `report.designer.conditionalColor.${valueCode}Value`
			// #57905
			// Replace the default "Auto" placeholder with the result of using
			// the message code
			placeholder = ` ${i18n.get(msgCode)}`
		}
		return placeholder
	},

	updatePlaceholder: function() {
		const placeholder = this.getPlaceholder()
		this.$('input').attr('placeholder', placeholder)
	},

	updateMarkedField: function () {
		// #59369 for gauge widgets, we want to update the validators of existing
		// threshold fields when more segments have been added, so we won't
		// any fields mistakenly marked as required
		if (this.isGaugeWidget) {
			this.markedField.clearError()
			this.markedField.options.validator = this._validator()
		}
	},

	_changeInput: function(e) {
		if (!this.validate()) {
			this.trigger('change', this.markedField.val())
		}
	},

	validate: function() {
		let v = this.markedField.val()
		if (this.markedField.validate(v)) { return }
		//if validation passed, update the thresholds and update the validator
		let val = isNaN(v) ? null : v
		this.value = val
		this.thresholds.value(val, this.index)
		this.markedField.options.validator = this._validator()
		this.markedField.clearError()
	},

	remove: function() {
		this.markedField.remove()
		Backbone.View.prototype.remove.apply(this, arguments)
	}

})

export default ThresholdField
ThresholdField.__filename__ = 'components/threshold-colors/threshold-field.js'
